/**
 * Reusable Form Modal Component
 * Generic modal for add/edit operations
 */

import type { ReactNode } from 'react';
import PopupHeader from './PopupHeader';
import { PopupHeader as WindowPopupHeader, usePopupWindow } from '../popup/PopupHeader';

interface FormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  title: string;
  children: ReactNode;
  submitText?: string;
  submitColor?: 'violet' | 'blue' | 'green' | 'purple' | 'cyan' | 'orange' | 'indigo' | 'pink' | 'amber' | 'emerald' | 'rose' | 'red';
  isSubmitting?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Label for the dismiss button (defaults to "Cancel"). */
  cancelText?: string;
  /** Extra footer buttons rendered to the left of Cancel/Submit (e.g. a Reset button). */
  extraActions?: ReactNode;
  /**
   * Draw the header as a coloured band (icon tile, white title) instead of the light bar.
   * Opt-in: a caller that does not pass it keeps the light bar exactly as before.
   */
  banner?: { icon?: string; background?: string };
  /**
   * Use the shared module popup header instead (components/popup/PopupHeader): the
   * module's gradient, icon tile, minimize / maximize / close. Opt-in, and wins over
   * `banner` — a caller that does not pass it keeps its header exactly as before.
   */
  popup?: { gradient: string; icon?: string; subtitle?: ReactNode };
}

export default function FormModal({
  isOpen,
  onClose,
  onSubmit,
  title,
  children,
  submitText = 'Save',
  submitColor = 'violet',
  isSubmitting = false,
  size = 'md',
  cancelText = 'Cancel',
  extraActions,
  banner,
  popup,
}: FormModalProps) {
  // Called before the early return so the hook order never changes.
  const win = usePopupWindow(isOpen);
  if (!isOpen) return null;
  const max = !!popup && win.isMaximized;

  const sizeClasses = {
    sm: 'max-w-lg',
    md: 'max-w-2xl',
    lg: 'max-w-4xl',
    xl: 'max-w-6xl',
  };

  const colorClasses = {
    violet: 'bg-violet-600 hover:bg-violet-700',
    blue: 'bg-blue-600 hover:bg-blue-700',
    green: 'bg-green-600 hover:bg-green-700',
    purple: 'bg-purple-600 hover:bg-purple-700',
    cyan: 'bg-cyan-600 hover:bg-cyan-700',
    orange: 'bg-orange-600 hover:bg-orange-700',
    indigo: 'bg-indigo-600 hover:bg-indigo-700',
    pink: 'bg-pink-600 hover:bg-pink-700',
    amber: 'bg-amber-600 hover:bg-amber-700',
    emerald: 'bg-emerald-600 hover:bg-emerald-700',
    rose: 'bg-rose-600 hover:bg-rose-700',
    red: 'bg-red-600 hover:bg-red-700',
  };

  return (
    <div className={popup ? win.overlayClass('fixed inset-0 z-50 overflow-y-auto') : 'fixed inset-0 z-50 overflow-y-auto'}>
      <div className={`flex items-center justify-center min-h-screen text-center sm:block ${max ? '' : 'px-4 pt-4 pb-20 sm:p-0'}`}>
        {/* Overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-black/40 backdrop-blur-sm"
          onClick={onClose}
        ></div>

        {/* Center modal */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

        {/*
          Modal panel.

          Capped at 90vh and laid out as a column so the header and footer stay put and
          only the FIELDS scroll. Without the cap a tall form (the refund form is the
          first to reach it) grows past both ends of the viewport: `align-middle`
          centres the panel, so the title is pushed off the top of the screen and the
          Save button off the bottom, and the outer scroll container is the only way
          back to either. A short modal is unaffected — max-height never shrinks it.
        */}
        <div className={win.panelClass(`inline-flex flex-col w-full ${sizeClasses[size]} max-h-[90vh] my-4 sm:my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-2xl rounded-xl`)}>
          {/* Header — the module popup header, the caller's coloured band, or the slim light bar */}
          {popup ? (
            <WindowPopupHeader
              window={win}
              gradient={popup.gradient}
              icon={popup.icon}
              title={title}
              subtitle={popup.subtitle}
              onClose={onClose}
              closeDisabled={isSubmitting}
            />
          ) : (
            <PopupHeader
              title={title}
              icon={banner?.icon}
              tone={banner ? 'band' : 'light'}
              background={banner ? (banner.background ?? 'linear-gradient(120deg, #7c3aed 0%, #4a3aa7 100%)') : undefined}
              onClose={onClose}
              closeDisabled={isSubmitting}
            />
          )}

          {/* Form */}
          <form onSubmit={onSubmit} className="flex flex-col min-h-0 flex-1">
            {/* Form fields — the only part that scrolls */}
            <div className="space-y-4 px-6 py-5 overflow-y-auto flex-1 min-h-0">{children}</div>

            {/* Footer — sleek light bar */}
            <div className="flex items-center justify-end gap-2 px-6 py-2.5 bg-gray-50 border-t border-gray-200 flex-shrink-0">
              {extraActions}
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="px-4 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-400 disabled:opacity-50 transition-colors"
              >
                {cancelText}
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`px-4 py-1.5 text-xs font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:opacity-50 flex items-center transition-colors ${colorClasses[submitColor] || colorClasses.violet}`}
              >
                {isSubmitting && (
                  <svg className="animate-spin -ml-0.5 mr-1.5 h-3.5 w-3.5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {submitText}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

